import { Injectable, inject } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { UserService } from "../../services/user.service";

@Injectable()
export class BaseRouteGuard implements CanActivate, CanActivateChild {
  private static readonly LOGIN_URL = "/pages/lock";
  private static readonly ADMINISTRATOR_ROLE = "ADMINISTRATOR";

  private readonly router = inject(Router);

  public constructor(protected authentication: UserService) {}

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    const user = this.authentication.getIdentity();

    if (!this.authentication.isAuthenticated() || !user) {
      return this.router.createUrlTree([BaseRouteGuard.LOGIN_URL], {
        queryParams: { returnUrl: state.url },
      });
    }

    if (
      user.role === BaseRouteGuard.ADMINISTRATOR_ROLE ||
      this.checkPermissions(state.url)
    ) {
      return true;
    }

    return this.redirectToHome(user, state.url);
  }

  public canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    return this.canActivate(route, state);
  }

  public checkPermissions(url: string): boolean {
    const user = this.authentication.getIdentity();

    if (!user) {
      return false;
    }

    const permissions = this.parsePermissions(user.userperms);
    const master = this.parsePermissions(user.masterPerm);

    return this.checkModule(url, permissions) && this.checkModule(url, master);
  }

  public checkModule(url: string, modulos: number[] | null): boolean {
    if (!modulos) {
      return false;
    }
    return modulos.indexOf(this.changeUrlModuleToID(url)) >= 0;
  }

  private redirectToHome(user: any, currentUrl: string): boolean | UrlTree {
    const homeUrl = this.resolveHomeUrl(user);
    const currentPath = currentUrl.split(/[?#]/)[0];

    if (currentPath === homeUrl) {
      return false;
    }

    return this.router.createUrlTree([homeUrl]);
  }

  private resolveHomeUrl(user: any): string {
    const role = String(user.role || "").toUpperCase();

    if (role === "VENDOR") {
      return "/homeclient";
    }

    if (role === "CUSTOMER") {
      return this.checkPermissions("/sales-deals-client")
        ? "/sales-deals-client"
        : "/tables/listfiles";
    }

    return "/dashboard";
  }

  private parsePermissions(raw: string): number[] | null {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  public changeUrlModuleToID(url: string): number {
    if (url.includes("/agenda")) {
      return 1;
    } else if (url.includes("/tables/listcompanies")) {
      return 2;
    } else if (url.includes("/vendors")) {
      return 3;
    } else if (url.includes("/tables/listfiles")) {
      return 4;
    } else if (url.includes("/hdchecker")) {
      return 5;
    } else if (url.includes("/tables/listpagos")) {
      return 6;
    } else if (url.includes("/tables/listpersonal")) {
      return 7;
    } else if (url.includes("/purchase-order")) {
      return 8;
    } else if (url.includes("/tables/listcontactform")) {
      return 9;
    } else if (url.includes("/trucks")) {
      return 10;
    } else if (url.includes("/sign-sheet")) {
      return 11;
    } else if (url.includes("/reports")) {
      return 12;
    } else if (url.includes("/scrap-inventory")) {
      return 13;
    } else if (url.includes("/processing-instructions")) {
      return 14;
    } else if (url.includes("/permissions")) {
      return 15;
    } else if (url.includes("/tables/listlogs")) {
      return 16;
    } else if (url.includes("/mycontactforms")) {
      return 17;
    } else if (url.includes("/tables/listcompanyfiles")) {
      return 30;
    } else if (url.includes("/customers")) {
      return 35;
    } else if (url.includes("/categories")) {
      return 36;
    } else if (url.includes("/Ecommerce") || url.includes("/gobig")) {
      return 37;
    } else if (url.includes("/testing-record")) {
      return 38;
    } else if (url.includes("/holidays")) {
      return 40;
    } else if (url.includes("/quickbooksconnect")) {
      return 41;
    } else if (url.includes("/master-items")) {
      return 46;
    } else if (url.includes("/psa")) {
      return 47;
    } else if (url.includes("/manufacturer")) {
      return 49;
    } else if (url.includes("/master-types")) {
      return 50;
    } else if (url.includes("/productCategory")) {
      return 51;
    } else if (url.includes("/sales-orders")) {
      return 52;
    } else if (url.includes("/warehouse")) {
      return 48;
    } else if (url.includes("/notifications-perms")) {
      return 54;
    } else if (url.includes("/system-setup") || url.includes("setup")) {
      return 55;
    } else if (url.includes("/sale-terms")) {
      return 56;
    } else if (url.includes("/testing-record")) {
      return 57;
    } else if (url.includes("/write-downs")) {
      return 58;
    } else if (url.includes("/sync-quickbooks")) {
      return 59;
    } else if (url.includes("/questionnaire")) {
      return 61;
    } else if (url.includes("/container-inventory")) {
      return 64;
    } else if (url.includes("/tickets")) {
      return 65;
    } else if (url.includes("/insidelocation")) {
      return 66;
    } else if (url.includes("/purchaseproposal")) {
      return 67;
    } else if (url.includes("/logsSystem")) {
      return 70;
    } else if (url.includes("/contactformblacklist")) {
      return 76;
    } else if (url.includes("/prouct-conditions")) {
      return 77;
    } else if (url.includes("/product-inventory")) {
      return 78;
    } else if (url.includes("/weightbalance")) {
      return 79;
    } else if (url.includes("/taxauthorities")) {
      return 81;
    } else if (url.includes("/anonymusreporting")) {
      return 86;
    } else if (url.includes("/salesproposal")) {
      return 87;
    } else if (url.includes("/purchase-terms")) {
      return 94;
    } else if (url.includes("/forms-reports")) {
      return 95;
    } else if (url.includes("/reminders")) {
      return 96;
    } else if (url.includes("/priceHistory")) {
      return 100;
    } else if (url.includes("/securaze")) {
      return 101;
    } else if (url.includes("/invoices")) {
      return 102;
    } else if (url.includes("/shipments")) {
      return 103;
    } else if (url.includes("/statistics")) {
      return 104;
    } else if (url.includes("/workflow-process")) {
      return 105;
    } else if (url.includes("/centralizedfiles")) {
      return 106;
    } else if (url.includes("/visitorlink")) {
      return 107;
    } else if (url.includes("/erasuremethods")) {
      return 108;
    } else if (url.includes("/tagging-groups")) {
      return 109;
    } else if (url.includes("/inventoryhistory")) {
      return 110;
    } else if (url.includes("/configpto")) {
      return 111;
    } else if (url.includes("/containerstype")) {
      return 112;
    } else if (url.includes("/visibilitytables")) {
      return 114;
    } else if (url.includes("/backgroundprocess")) {
      return 116;
    } else if (url.includes("/batch-container-inventory")) {
      return 117;
    } else if (url.includes("/inventoryReportExtraMissing")) {
      return 119;
    } else if (url.includes("/inventoryReportExpenses")) {
      return 120;
    } else if (url.includes("/files-permissions-groups")) {
      return 121;
    } else if (url.includes("/inventoryReportsDownloadManager")) {
      return 145;
    } else if (url.includes("/inventoryReportsComparative")) {
      return 146;
    } else if (url.includes("/live-reporting/price-history-v2")) {
      return 147;
    } else if (url.includes("/returns")) {
      return 148;
    } else if (url.includes("/inventory-client")) {
      return 149;
    } else if (url.includes("/live-reporting/inventory-reports-v2")) {
      return 154;
    } else if (url.includes("/live-reporting/extra-missing-report-v2")) {
      return 155;
    } else if (url.includes("/live-reporting/expense-reports-v3")) {
      return 156;
    } else if (url.includes("/live-reporting/comparison-inventory-reports-v2")) {
      return 157;
    } else if (url.includes("/inventoryReports")) {
      return 118;
    } else if (url.includes("/inventory")) {
      return 99;
    } else if (url.includes("/buybacks")) {
      return 162;
    } else if (url.includes("/leadlocation")) {
      return 180;
    } else if (url.includes("/pickuplocation")) {
      return 181;
    } else if (url.includes("/vendorlocation")) {
      return 182;
    } else if (url.includes("/vendor-review")) {
      return 183;
    } else if (url.includes("/bug-reporting")) {
      return 184;
    } else if (url.includes("/sales-deals-client")) {
      return 209;
    } else if (url.includes("/sales-deals")) {
      return 204;
    } else if (url.includes("/shipment-scheduler")) {
      return 205;
    } else if (url.includes("/emails")) {
      return 211;
    } else if (url.includes("/v2-quickbooks")) {
      return 213;
    } else if (url.includes("/e-commerce/listings")) {
      return 214;
    } else if (url.includes("/e-commerce/new-listing")) {
      return 215;
    } else if (url.includes("/auctions")) {
      return 218;
    } else if (url.includes("/bid-marketplace")) {
      return 219;
    } else if (url.includes("/bid")) {
      return 219;
    }
    return 0;
  }
}

export interface ChangeUrlToPermissions {
  changeUrlModuleToID(url: string): number;
}